import React, { useEffect, useState } from "react";
import { Box, IconButton, Tooltip } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import FilterListIcon from "@mui/icons-material/FilterList";
import {
  MaterialReactTable,
  MRT_ShowHideColumnsButton,
  MRT_ToggleDensePaddingButton,
  MRT_ToggleFiltersButton,
  MRT_GlobalFilterTextField,
} from "material-react-table";
import dayjs from "dayjs";
import { filterFnsDict } from "./filter_fns";
import { sortingFnsDict } from "./sorting_fns";

const ResetFiltersComponent = ({ clearFilters, activeFilterCount }) => (
  <Tooltip title="Clear all active filters">
    <IconButton
      onClick={clearFilters}
      aria-label="clear all active filters"
      sx={{
        width: 40,
        height: 40,
        borderRadius: "50%", // Rounded corners to match MRT buttons
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: 0,
        position: "relative",
        "&:hover": {
          backgroundColor: "rgba(0, 0, 0, 0.08)", // Same hover background as MRT buttons
          borderRadius: "50%", // Ensure hover has rounded corners
        },
      }}
    >
      <FilterListIcon
        sx={{
          color: "text.secondary",
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
        }}
      />
      <ClearIcon
        sx={{
          color: "#f44336",
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          opacity: 0.5,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          top: -1.5,
          right: -1.5,
          backgroundColor: "text.secondary",
          color: "white",
          borderRadius: "50%",
          width: 16,
          height: 16,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          fontSize: "0.6rem",
          fontWeight: "bold",
        }}
      >
        {activeFilterCount}
      </Box>
    </IconButton>
  </Tooltip>
);

function ReactTableWithSettings({
  unique_table_key,
  columns,
  data,
  error,
  isLoading,
  defaultVisibility = {},
  defaultSorting = [],
  topToolbarComponent = null,
  rowProps = (row) => ({}),
  defaultFilters = null,
  customSorting = null,
  additionalTopToolbarComponent = null,
  initialGlobalFilter = "",
  onGlobalFilterChangeExternal = null,
  externalColumnFilters = null,
  onClearFilters = null,
}) {
  const [activeFilterCount, setActiveFilterCount] = useState(0);

  function clearFilters() {
    setColumnFilters([]);
    if (onClearFilters) onClearFilters();
  }

  const [columnFilters, setColumnFilters] = useState(() => {
    let savedFilters = localStorage.getItem(unique_table_key + "_column_filters");
    let activeFilters = null;
    if (defaultFilters) {
      // Take filters passed from parent component
      setActiveFilterCount(defaultFilters.length);
      activeFilters = defaultFilters;
    } else {
      activeFilters = savedFilters ? JSON.parse(savedFilters) : [];
    }

    // THIS LOGIC ASSUMES THAT EACH DATE COLUMN HAS a '_DATE' IN ITS KEY!!
    activeFilters.forEach((filter) => {
      if (filter.id && filter.id.includes("_date") && Array.isArray(filter.value)) {
        filter.value = filter.value.map((dateString) => dayjs(dateString));
      }
    });

    return activeFilters;
  });

  const [density, setDensity] = useState(() => {
    const savedDensity = localStorage.getItem(unique_table_key + "_density");
    return savedDensity ? JSON.parse(savedDensity) : "compact";
  });

  const [columnVisibility, setColumnVisibility] = useState(() => {
    const savedVisibility = localStorage.getItem(unique_table_key + "_column_visibility");
    if (savedVisibility) {
      const parsed = JSON.parse(savedVisibility);
      // Merge any new defaultVisibility keys missing from saved settings
      for (const key in defaultVisibility) {
        if (!(key in parsed)) {
          parsed[key] = defaultVisibility[key];
        }
      }
      return parsed;
    }
    return defaultVisibility;
  });

  const [sorting, setSorting] = useState(() => {
    const savedSorting = localStorage.getItem(unique_table_key + "_sorting");
    return customSorting ? customSorting : savedSorting ? JSON.parse(savedSorting) : defaultSorting;
  });

  const [pagination, setPagination] = useState(() => {
    const savedPagination = localStorage.getItem(unique_table_key + "_pagination");
    return savedPagination ? JSON.parse(savedPagination) : { pageIndex: 0, pageSize: 10 };
  });

  const [columnOrder, setColumnOrder] = useState(() => {
    const savedColumnOrder = localStorage.getItem(unique_table_key + "_column_order");
    if (savedColumnOrder) {
      const parsed = JSON.parse(savedColumnOrder);
      const defaultOrder = columns.map((col) => col.accessorKey || col.id);
      // Insert any new columns not in the saved order at their default position
      for (const col of defaultOrder) {
        if (!parsed.includes(col)) {
          const defaultIdx = defaultOrder.indexOf(col);
          parsed.splice(defaultIdx, 0, col);
        }
      }
      return parsed;
    }
    return columns.map((col) => col.accessorKey || col.id);
  });

  const [globalFilter, setGlobalFilter] = useState(initialGlobalFilter || "");

  const handleGlobalFilterChange = (newGlobalFilter) => {
    setGlobalFilter(newGlobalFilter);
    if (onGlobalFilterChangeExternal) {
      onGlobalFilterChangeExternal(newGlobalFilter);
    }
  };

  useEffect(() => {
    // Allows a parent component to drive specific column filters (e.g. a toolbar toggle)
    // instead of the column's own filter UI.
    if (!externalColumnFilters) return;
    setColumnFilters((old) => {
      const untouched = old.filter((f) => !externalColumnFilters.some((ext) => ext.id === f.id));
      const applied = externalColumnFilters
        .filter((ext) => ext.value !== undefined)
        .map((ext) => ({ id: ext.id, value: ext.value }));
      return [...untouched, ...applied];
    });
  }, [externalColumnFilters]);

  useEffect(() => {
    // When you open the column filter menu, some filters will be initialized to null
    // Don't need to track that state and don't want to track them in the active filter count
    const validFilters = columnFilters.filter(
      (filter) =>
        !(
          Array.isArray(filter.value) &&
          filter.value.length <= 2 && // 2 will do for now (range filters)
          filter.value.every((val) => val === undefined)
        ),
    );
    localStorage.setItem(unique_table_key + "_column_filters", JSON.stringify(validFilters));
    setActiveFilterCount(validFilters.length);
  }, [columnFilters, unique_table_key]);

  useEffect(() => {
    localStorage.setItem(unique_table_key + "_density", JSON.stringify(density));
  }, [density, unique_table_key]);

  useEffect(() => {
    localStorage.setItem(unique_table_key + "_column_visibility", JSON.stringify(columnVisibility));
  }, [columnVisibility, unique_table_key]);

  useEffect(() => {
    localStorage.setItem(unique_table_key + "_sorting", JSON.stringify(sorting));
  }, [sorting, unique_table_key]);

  useEffect(() => {
    localStorage.setItem(unique_table_key + "_pagination", JSON.stringify(pagination));
  }, [pagination, unique_table_key]);

  useEffect(() => {
    localStorage.setItem(unique_table_key + "_column_order", JSON.stringify(columnOrder));
  }, [columnOrder, unique_table_key]);

  if (error) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <div>Error loading data</div>;
      </Box>
    );
  }
  columns = columns.map((column) => ({
    ...column,
    // This will set a default filterFn if there is not a filterVariant defined!
    // weakStringContainsFilter is defined via: filterFns={filterFnsDict}
    ...(column.filterVariant == null &&
      column.filterFn == null && {
        filterFn: "weakStringContainsFilter",
      }),
    // Update the default header
    Header: ({ column }) => (
      <div>
        {/* Limit the width of a column and create an ellipsis textOverflow */}
        {/* Add a tooltip to show the full name */}
        <Tooltip title={column.columnDef.header}>
          <span
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "inline-block", // Ensure truncation
              maxWidth: "250px",
            }}
          >
            {column.columnDef.header}
          </span>
        </Tooltip>
      </div>
    ),
  }));

  return (
    <Box
      sx={{
        // Change the column header to a column pushing the actions to a second row
        ".Mui-TableHeadCell-Content": {
          display: "flex",
          alignItems: "flex-start", // Ensure left alignment
          bgcolor: "white",
        },
        // Hide this action, it's already part of the column actions and we don't want clutter
        ".MuiTableSortLabel-root": {
          display: "none",
        },
      }}
    >
      <MaterialReactTable
        columns={columns}
        data={data || []}
        filterFns={filterFnsDict}
        sortingFns={sortingFnsDict}
        // This needs to match either one of the default filter keys or
        // one set by the dict provided to filterFns above
        globalFilterFn={"weakStringContainsFilter"}
        columnFilterFn={"weakStringContainsFilter"}
        enableGlobalFilter={true}
        getColumnCanGlobalFilter={(column) => {
          // Enable global filter for all columns except those explicitly disabled
          return column.columnDef.enableGlobalFilter !== false;
        }}
        enableFilterMatchHighlighting={true} // leaving this on for now, disable if it's too compute intensive
        muiTableBodyCellProps={{
          sx: {
            bgcolor: "white", // Set background color for the table body
          },
        }}
        muiTableHeadCellProps={{
          sx: {
            bgcolor: "white", // Set background color for the table header
          },
        }}
        state={{
          columnVisibility,
          columnFilters,
          globalFilter,
          sorting,
          pagination,
          columnOrder,
          isLoading,
          density,
          showGlobalFilter: true, // makes the global filter expanded by default
        }}
        onGlobalFilterChange={handleGlobalFilterChange}
        onColumnFiltersChange={setColumnFilters}
        onColumnVisibilityChange={setColumnVisibility}
        onSortingChange={setSorting}
        onDensityChange={setDensity}
        onPaginationChange={setPagination}
        onColumnOrderChange={setColumnOrder}
        enableColumnOrdering={true}
        renderTopToolbar={({ table }) => (
          <Box
            sx={{ display: "flex", alignItems: "center", gap: "1rem", p: "0.5rem", justifyContent: "space-between" }}
          >
            {topToolbarComponent}
            <Box sx={{ display: "flex", alignItems: "center", gap: "1rem" }}>
              <MRT_GlobalFilterTextField table={table} />
              {additionalTopToolbarComponent}
              <MRT_ShowHideColumnsButton table={table} />
              <ResetFiltersComponent clearFilters={clearFilters} activeFilterCount={activeFilterCount} />
              <MRT_ToggleFiltersButton table={table} />
              <MRT_ToggleDensePaddingButton table={table} />
            </Box>
          </Box>
        )}
        muiTableBodyRowProps={({ row }) => ({
          ...rowProps(row),
        })}
        muiSkeletonProps={{
          animation: "pulse",
          width: "100%",
        }}
      />
    </Box>
  );
}

export default ReactTableWithSettings;
